import React, { useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

import {
    NPDManagerHead,
    generatProfilePicUrl,
    productionManager,
    sourcingManagerHead
} from "../../Utils/Constant";
// import { generatProfilePicUrl } from "@/utils/imageUtils"; // Update path as per your project

// Types
interface TeamMember {
    User_Name: string;
    User_Email: string;
    User_ID: string;
    Display_Pic: string;
    User_Role?: string;
}

interface SelectedMember {
    name: string;
    avatar: string;
    userId: string;
    userEmail: string;
    rfqId: string;
    coRfqId: string;
}

interface ActionMenuProps {
    rowsSM: TeamMember[];
    fetchAssignedDetails: SelectedMember[];
    params: any; // Update type if needed
    onAssign?: (member: SelectedMember) => Promise<void>;
    onRemove?: (params: any) => Promise<void>;
    feasibleAction: boolean;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
    rowsSM,
    fetchAssignedDetails,
    params,
    onAssign,
    onRemove,
    feasibleAction
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorElCTM, setAnchorElCTM] = useState<null | HTMLElement>(null);
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
    const [selectedMember, setSelectedMember] = useState<SelectedMember | null>(
        fetchAssignedDetails?.[0] || null
    );
    const [isAssigning, setIsAssigning] = useState(false);

    const handleClickAssign = (event: React.MouseEvent<HTMLElement>) => {
        if (!selectedMember) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClickCTM = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElCTM(event.currentTarget);
    };

    const handleCloseMenus = () => {
        setAnchorEl(null);
        setAnchorElCTM(null);
    };

    const handleSelect = async (member: SelectedMember) => {
        setIsAssigning(true);
        try {
            if (onAssign) await onAssign(member);
            setSelectedMember(member);
        } catch (error) {
            console.error("Assignment failed", error);
        } finally {
            setIsAssigning(false);
            setAnchorEl(null);
        }
    };

    const handleOpenDialog = () => {
        setOpenRemoveDialog(true);
        setAnchorElCTM(null);
    };

    const handleCloseDialog = () => {
        setOpenRemoveDialog(false);
    };

    const handleRemoveMember = async () => {
        setIsAssigning(true);
        try {
            if (onRemove) await onRemove(params);
            setSelectedMember(null);
        } catch (error) {
            console.error("Removal failed", error);
        } finally {
            setIsAssigning(false);
            handleCloseDialog();
        }
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={2}
            justifyContent="space-between"
            width="100%"
        >
            {/* Assign/Assigned */}
            <Tooltip
                title={
                    feasibleAction &&
                    (selectedMember
                        ? "Assigned Team Member"
                        : "Assign Team Member")
                }
            >
                <Box
                    onClick={handleClickAssign}
                    sx={{
                        cursor: feasibleAction ? "pointer" : "text",
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    {selectedMember ? (
                        <>
                            <Avatar
                                src={selectedMember.avatar}
                                sx={{ width: 28, height: 28 }}
                            />
                            <Typography variant="body2">
                                {selectedMember.name}
                            </Typography>
                        </>
                    ) : (
                        <>
                            {feasibleAction ? (
                                <>
                                    <PersonAddAltIcon
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            backgroundColor: "#BDBDBD",
                                            borderRadius: "50%",
                                            padding: "2px",
                                            color: "#fff"
                                        }}
                                    />
                                    <Typography color="primary" variant="body2">
                                        ASSIGN
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <PersonAddAltIcon
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            backgroundColor: "#BDBDBD",
                                            borderRadius: "50%",
                                            padding: "2px",
                                            color: "#fff"
                                        }}
                                    />
                                    <Typography variant="body2">
                                        Not Assigned
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Tooltip>

            {/* 3-dot menu */}

            {feasibleAction &&
                selectedMember &&
                params?.status === "Internal RFQ Not Created" && (
                    <>
                        <Tooltip title="">
                            <IconButton
                                onClick={handleClickCTM}
                                sx={{
                                    "& MuiIconButton-root:hover": {
                                        background: "none"
                                    }
                                }}
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorElCTM}
                            open={Boolean(anchorElCTM)}
                            onClose={handleCloseMenus}
                        >
                            <MenuItem onClick={handleOpenDialog}>
                                <Typography variant="body2">
                                    Change team member
                                </Typography>
                            </MenuItem>
                        </Menu>
                    </>
                )}

            {/* Team members list */}
            {feasibleAction && (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenus}
                >
                    {rowsSM != null &&
                        rowsSM !== undefined &&
                        rowsSM?.length > 0 &&
                        rowsSM?.map((member, index) => {
                            const item = {
                                name: member.User_Name,
                                avatar: generatProfilePicUrl(
                                    member.User_ID,
                                    member.Display_Pic,
                                    "Profile_Picture"
                                ),
                                userId: member.User_ID,
                                userEmail: member.User_Email,
                                rfqId: params?.ID,
                                coRfqId: params?.rfqID
                            };

                            return (
                                <MenuItem
                                    key={index}
                                    onClick={() => handleSelect(item)}
                                    disabled={isAssigning}
                                >
                                    <Box display="flex" alignItems="center">
                                        <Avatar
                                            src={item.avatar}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                marginRight: 1
                                            }}
                                        />
                                        <Typography variant="body2">
                                            {item.name}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                </Menu>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={openRemoveDialog} onClose={handleCloseDialog}>
                <DialogTitle fontWeight={"bold"}>
                    Remove & Change Team Member
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "text.secondary" }}>
                        Are you sure you want to remove{" "}
                        <strong>{selectedMember?.name}</strong> for{" "}
                        <strong>{selectedMember?.coRfqId}</strong> ? This will
                        revoke their access to the{" "}
                        <strong>{selectedMember?.coRfqId}</strong>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="error">
                        Cancel
                    </Button>
                    <Button onClick={handleRemoveMember} disabled={isAssigning}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ActionMenu;
